import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { GridSearch } from '../../../components/GridSearch';

/** The minimal site shape the picker needs — structurally satisfied by the
 *  Sample Login config's `sampleSites` (LoginLookupOpt). */
export interface StationOpt {
  oid: string;
  name: string;
  address: string;
  keyMap: string;
  pwsId: string;
  samplePointId: string;
  samplePointType: string;
  waterTypeName: string;
}

const COLUMNS: AppColumn<StationOpt>[] = [
  { field: 'name',            header: 'SiteName',        width: 200 },
  { field: 'address',         header: 'Address',         width: 170 },
  { field: 'keyMap',          header: 'KeyMap',          width: 100 },
  { field: 'pwsId',           header: 'PWSID',           width: 100 },
  { field: 'samplePointId',   header: 'SamplePointID',   width: 130 },
  { field: 'samplePointType', header: 'SamplePointType', width: 140 },
  { field: 'waterTypeName',   header: 'WaterType',       width: 120 },
];

/**
 * "Alternative Station" popup behind the AlternativeStation cell of the Sample
 * Login grid — legacy ALPACA popup parity (SampleRegistrationViewController
 * StationLocation/AlternativeStation grid-lookup): a searchable grid of the
 * client's Sample Sites (SiteName · Address · KeyMap · PWSID · SamplePointID ·
 * SamplePointType · WaterType), single-select, OK / Cancel. Clicking a row picks
 * it at once; ticking one + OK does the same. Built from the shared AppDialog +
 * AppDataGrid + GridSearch (user request 2026-09-26).
 */
export function StationPickerDialog({ open, sites, currentOid, onPick, onClose }: {
  open: boolean;
  /** The client's sites (Sample Login config `sampleSites`). */
  sites: StationOpt[];
  /** The row's current AlternativeStation Oid — ticked when the popup opens. */
  currentOid: string;
  onPick: (site: StationOpt) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  // Every opening starts clean, with the row's current choice ticked.
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelected(currentOid ? new Set([currentOid]) : new Set());
  }, [open, currentOid]);

  const rows = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return sites;
    return sites.filter(s =>
      s.name.toLowerCase().includes(t) ||
      s.address.toLowerCase().includes(t) ||
      s.keyMap.toLowerCase().includes(t) ||
      s.pwsId.toLowerCase().includes(t) ||
      s.samplePointId.toLowerCase().includes(t) ||
      s.samplePointType.toLowerCase().includes(t) ||
      s.waterTypeName.toLowerCase().includes(t));
  }, [sites, search]);

  const ticked = sites.find(s => selected.has(s.oid));

  return (
    <AppDialog
      open={open}
      title="Alternative Station"
      width={860}
      className="dlg-center"
      maximizable
      onClose={onClose}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={!ticked}
            onClick={() => { if (ticked) onPick(ticked); }}>
            OK
          </button>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        </>
      }
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
        <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
      </div>
      <AppDataGrid<StationOpt>
        rows={rows}
        columns={COLUMNS}
        getRowId={s => s.oid}
        defaultPageSize={20}
        initialSort={{ key: 'name', dir: 'asc' }}
        selectable
        selectedIds={selected}
        onSelectionChange={(next) => {
          // Single-select: keep only the most-recently-ticked row.
          const added = [...next].filter(id => !selected.has(id));
          if (added.length) setSelected(new Set([added[added.length - 1]!]));
          else setSelected(new Set());
        }}
        onRowClick={onPick}
        emptyText="No sample sites for this client"
      />
    </AppDialog>
  );
}
